import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaSearch, FaFilter, FaTimes, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { collection, query, where, orderBy, getDocs, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase';
import { setProducts, setLoading, setError } from '../../features/marketplace/marketplaceSlice';
import ProductCard from './ProductCard';
import { SEOHead } from '../SEO';

const Container = styled.div`
  color: white;
  padding: 1rem 0;
  
  @media (max-width: 768px) {
    padding: 0.8rem 0;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0;
  }
`;

const SearchHeader = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(5px);
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 10px;
  }
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.8rem;
    margin-bottom: 1.2rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.6rem;
    margin-bottom: 1rem;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 0.9rem 1.2rem;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.1);
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.3), inset 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 0.8rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem 0.9rem;
    font-size: 0.95rem;
  }
`;

const SearchButton = styled(motion.button)`
  background: linear-gradient(90deg, #4a90e2, #357abd);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
  transition: all 0.3s ease;
  
  svg {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  }
  
  &:hover {
    background: linear-gradient(90deg, #357abd, #2868a9);
    box-shadow: 0 6px 16px rgba(74, 144, 226, 0.4);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 2px 8px rgba(74, 144, 226, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 0.8rem 1.2rem;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem 1rem;
    font-size: 0.95rem;
  }
`;

const FilterButton = styled(motion.button)`
  background: ${props => props.active ? 'linear-gradient(90deg, #4a90e2, #357abd)' : 'rgba(255, 255, 255, 0.08)'};
  color: white;
  border: ${props => props.active ? 'none' : '1px solid rgba(255, 255, 255, 0.15)'};
  border-radius: 10px;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: ${props => props.active ? '500' : 'normal'};
  box-shadow: ${props => props.active ? '0 4px 12px rgba(74, 144, 226, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  transition: all 0.3s ease;
  
  svg {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  }
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(90deg, #357abd, #2868a9)' : 'rgba(255, 255, 255, 0.12)'};
    box-shadow: ${props => props.active ? '0 6px 16px rgba(74, 144, 226, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.15)'};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  @media (max-width: 768px) {
    padding: 0.8rem 1.2rem;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem 1rem;
    font-size: 0.95rem;
  }
`;

const FiltersContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    margin-bottom: 1.2rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 8px;
  }
`;

const FilterGroup = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: 600;
  color: #e0e0e0;
  
  svg {
    color: #4a90e2;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 0.7rem;
    gap: 0.4rem;
  }
`;

const FilterOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const FilterOption = styled.button`
  background: ${props => props.active ? 'linear-gradient(90deg, #4a90e2, #357abd)' : 'rgba(255, 255, 255, 0.08)'};
  color: white;
  border: ${props => props.active ? 'none' : '1px solid rgba(255, 255, 255, 0.15)'};
  border-radius: 20px;
  padding: 0.5rem 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: ${props => props.active ? '500' : 'normal'};
  box-shadow: ${props => props.active ? '0 2px 8px rgba(74, 144, 226, 0.3)' : 'none'};
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(90deg, #357abd, #2868a9)' : 'rgba(255, 255, 255, 0.12)'};
    transform: translateY(-1px);
    box-shadow: ${props => props.active ? '0 4px 10px rgba(74, 144, 226, 0.4)' : '0 2px 5px rgba(0, 0, 0, 0.1)'};
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.9rem;
    font-size: 0.9rem;
  }
`;

const PriceRangeContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const PriceInput = styled.input`
  width: 100px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.5rem;
  color: white;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const ApplyFiltersButton = styled(motion.button)`
  background: linear-gradient(90deg, #4a90e2, #357abd);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.8rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 1.5rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(90deg, #357abd, #2868a9);
    box-shadow: 0 6px 16px rgba(74, 144, 226, 0.4);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  @media (max-width: 768px) {
    padding: 0.7rem 1.2rem;
    font-size: 0.95rem;
    margin-top: 1.2rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
    margin-top: 1rem;
    border-radius: 8px;
  }
`;

const ClearFiltersButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.08);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 0.8rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 1.5rem;
  margin-left: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  @media (max-width: 768px) {
    padding: 0.7rem 1.2rem;
    font-size: 0.95rem;
    margin-top: 1.2rem;
    margin-left: 0.8rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
    margin-top: 1rem;
    margin-left: 0.6rem;
    border-radius: 8px;
  }
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  padding: 1rem 1.5rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 0.8rem 1.2rem;
    margin-bottom: 1.2rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem 1rem;
    margin-bottom: 1rem;
  }
`;

const ResultsCount = styled.div`
  font-size: 1rem;
  color: #e0e0e0;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
  
  @media (max-width: 480px) {
    gap: 0.7rem;
  }
`;

const SortLabel = styled.span`
  color: #e0e0e0;
  font-size: 0.9rem;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const SortSelect = styled.select`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 0.5rem 0.8rem;
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
  
  option {
    background: #0a1128;
    padding: 0.5rem;
  }
  
  @media (max-width: 768px) {
    flex: 1;
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.85rem;
  }
`;

const SortDirectionButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.08);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.15);
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  
  svg {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.12);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1.2rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 1rem;
    margin-bottom: 1.2rem;
  }
`;

const LoadMoreButton = styled(motion.button)`
  background: linear-gradient(90deg, #4a90e2, #357abd);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.9rem 2.5rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  display: block;
  margin: 0 auto;
  box-shadow: 0 4px 15px rgba(74, 144, 226, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: all 0.6s ease;
  }
  
  &:hover {
    background: linear-gradient(90deg, #357abd, #2868a9);
    box-shadow: 0 6px 20px rgba(74, 144, 226, 0.4);
    transform: translateY(-2px);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &:disabled {
    background: linear-gradient(90deg, #666, #555);
    cursor: not-allowed;
    box-shadow: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.8rem 2rem;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem 1.8rem;
    font-size: 0.95rem;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  color: #e0e0e0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  
  h3 {
    font-size: 1.4rem;
    margin-bottom: 0.8rem;
    font-weight: 600;
  }
  
  p {
    color: #ccc;
    font-size: 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 2.5rem;
    
    h3 {
      font-size: 1.3rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 2rem;
    border-radius: 10px;
    
    h3 {
      font-size: 1.2rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const MarketplaceSearch = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { products, loading, error } = useSelector(state => state.marketplace);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    priceMin: '',
    priceMax: '',
    rating: 0,
    tags: []
  });
  
  const [sortBy, setSortBy] = useState('newest');
  const [sortDirection, setSortDirection] = useState('desc');
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  
  const ITEMS_PER_PAGE = 12;
  
  useEffect(() => {
    fetchProducts();
  }, []);
  
  const fetchProducts = async (loadMore = false) => {
    try {
      dispatch(setLoading(true));
      
      let productsRef = collection(db, 'products');
      let constraints = [where('status', '==', 'active')];
      
      // Apply category filter
      if (filters.category !== 'all') {
        constraints.push(where('category', '==', filters.category));
      }
      
      // Apply price filters
      if (filters.priceMin && !isNaN(parseFloat(filters.priceMin))) {
        constraints.push(where('price', '>=', parseFloat(filters.priceMin)));
      }
      
      if (filters.priceMax && !isNaN(parseFloat(filters.priceMax))) {
        constraints.push(where('price', '<=', parseFloat(filters.priceMax)));
      }
      
      // Apply rating filter
      if (filters.rating > 0) {
        constraints.push(where('rating', '>=', filters.rating));
      }
      
      // Apply sort
      let sortField;
      switch (sortBy) {
        case 'price':
          sortField = 'price';
          break;
        case 'popular':
          sortField = 'purchaseCount';
          break;
        case 'rating':
          sortField = 'rating';
          break;
        case 'newest':
        default:
          sortField = 'createdAt';
      }
      
      let q;
      
      if (loadMore && lastVisible) {
        q = query(
          productsRef,
          ...constraints,
          orderBy(sortField, sortDirection),
          startAfter(lastVisible),
          limit(ITEMS_PER_PAGE)
        );
      } else {
        q = query(
          productsRef,
          ...constraints,
          orderBy(sortField, sortDirection),
          limit(ITEMS_PER_PAGE)
        );
      }
      
      const querySnapshot = await getDocs(q);
      
      // Check if there are more items to load
      setHasMore(querySnapshot.docs.length === ITEMS_PER_PAGE);
      
      // Set the last visible document for pagination
      if (querySnapshot.docs.length > 0) {
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      } else {
        setLastVisible(null);
      }
      
      const productsData = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        $id: doc.id
      }));
      
      // Filter by search term if provided
      let filteredProducts = productsData;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredProducts = productsData.filter(product => 
          product.name?.toLowerCase().includes(term) || 
          product.description?.toLowerCase().includes(term)
        );
      }
      
      if (loadMore) {
        dispatch(setProducts([...products, ...filteredProducts]));
      } else {
        dispatch(setProducts(filteredProducts));
      }
    } catch (err) {
      dispatch(setError(err.message));
    } finally {
      dispatch(setLoading(false));
    }
  };
  
  const handleSearch = () => {
    fetchProducts();
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  const handleFilterChange = (filterType, value) => {
    setFilters({
      ...filters,
      [filterType]: value
    });
  };
  
  const handleClearFilters = () => {
    setFilters({
      category: 'all',
      priceMin: '',
      priceMax: '',
      rating: 0,
      tags: []
    });
    setSearchTerm('');
  };
  
  const handleApplyFilters = () => {
    fetchProducts();
    setShowFilters(false);
  };
  
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    fetchProducts();
  };
  
  const toggleSortDirection = () => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    setSortDirection(newDirection);
    fetchProducts();
  };
  
  const handleLoadMore = () => {
    fetchProducts(true);
  };
  
  const categories = ['all', 'beats', 'songs', 'albums', 'merchandise'];
  // Define tags - ensure no superadmin-related tags are included
  const tags = ['hip-hop', 'afrobeat', 'reggae', 'gospel', 'dancehall', 'rnb'];
  // Filter out any tags that might contain 'admin' or 'superadmin'
  const filteredTags = tags.filter(tag => 
    !tag.toLowerCase().includes('admin') && 
    !tag.toLowerCase().includes('superadmin')
  );
  
  return (
    <Container>
      <SEOHead
        title="Marketplace Search"
        description="Search for music, beats, albums and more on Konzo Marketplace"
        keywords="music search, beats, songs, albums, konzo vibez, marketplace"
        canonicalUrl="/marketplace/search"
      />
      
      <SearchHeader>
        <SearchBar>
          <SearchInput 
            type="text" 
            placeholder="Search for beats, songs, albums..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <SearchButton 
            onClick={handleSearch}
            whileTap={{ scale: 0.95 }}
          >
            <FaSearch /> Search
          </SearchButton>
          <FilterButton 
            active={showFilters}
            onClick={() => setShowFilters(!showFilters)}
            whileTap={{ scale: 0.95 }}
          >
            {showFilters ? <FaTimes /> : <FaFilter />}
            {showFilters ? 'Hide Filters' : 'Filters'}
          </FilterButton>
        </SearchBar>
        
        {showFilters && (
          <FiltersContainer
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <FilterGroup>
              <FilterLabel>Category</FilterLabel>
              <FilterOptions>
                {categories.map(category => (
                  <FilterOption 
                    key={category}
                    active={filters.category === category}
                    onClick={() => handleFilterChange('category', category)}
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </FilterOption>
                ))}
              </FilterOptions>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Price Range</FilterLabel>
              <PriceRangeContainer>
                <PriceInput 
                  type="number" 
                  placeholder="Min" 
                  value={filters.priceMin}
                  onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                />
                <span>to</span>
                <PriceInput 
                  type="number" 
                  placeholder="Max" 
                  value={filters.priceMax}
                  onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                />
              </PriceRangeContainer>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Tags</FilterLabel>
              <FilterOptions>
                {filteredTags.map(tag => (
                  <FilterOption 
                    key={tag}
                    active={filters.tags.includes(tag)}
                    onClick={() => {
                      const newTags = filters.tags.includes(tag)
                        ? filters.tags.filter(t => t !== tag)
                        : [...filters.tags, tag];
                      handleFilterChange('tags', newTags);
                    }}
                  >
                    {tag.charAt(0).toUpperCase() + tag.slice(1)}
                  </FilterOption>
                ))}
              </FilterOptions>
            </FilterGroup>
            
            <div style={{ display: 'flex' }}>
              <ApplyFiltersButton
                onClick={handleApplyFilters}
                whileTap={{ scale: 0.95 }}
              >
                Apply Filters
              </ApplyFiltersButton>
              <ClearFiltersButton
                onClick={handleClearFilters}
                whileTap={{ scale: 0.95 }}
              >
                Clear All
              </ClearFiltersButton>
            </div>
          </FiltersContainer>
        )}
      </SearchHeader>
      
      <ResultsHeader>
        <ResultsCount>
          {loading ? 'Loading products...' : `${products.length} products found`}
        </ResultsCount>
        
        <SortContainer>
          <SortLabel>Sort by:</SortLabel>
          <SortSelect value={sortBy} onChange={handleSortChange}>
            <option value="newest">Newest</option>
            <option value="price">Price</option>
            <option value="popular">Popularity</option>
            <option value="rating">Rating</option>
          </SortSelect>
          <SortDirectionButton
            onClick={toggleSortDirection}
            whileTap={{ scale: 0.9 }}
          >
            {sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
          </SortDirectionButton>
        </SortContainer>
      </ResultsHeader>
      
      {loading && products.length === 0 ? (
        <p>Loading products...</p>
      ) : error ? (
        <p>Error: {error}</p>
      ) : products.length > 0 ? (
        <>
          <ProductsGrid>
            {products.map(product => (
              <ProductCard key={product.$id} product={product} />
            ))}
          </ProductsGrid>
          
          {hasMore && (
            <LoadMoreButton
              onClick={handleLoadMore}
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {loading ? 'Loading...' : 'Load More'}
            </LoadMoreButton>
          )}
        </>
      ) : (
        <NoResults>
          <h3>No products found</h3>
          <p>Try adjusting your search or filters</p>
        </NoResults>
      )}
    </Container>
  );
};

export default MarketplaceSearch;
